import React from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Users, DollarSign, Video, ShoppingBag, BookOpen, Check, X, Upload, TrendingUp, Crown, Lock, Unlock, Settings, RefreshCw, Globe, Edit3, FileSpreadsheet, AlertCircle, Download, Youtube, Plus, Layers, Calendar, Flag, Star, Trash2, Gift, Ban, UserX, UserCheck, CheckCircle, ShieldCheck, Clock, PartyPopper, Archive } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Directory tab.
 *
 * Lifted out of AdminPanel.js during the Phase 2 frontend split (Wave 4).
 * Pure presentational — receives parent state + handlers via props.
 */
export default function EdirectoryTab(props) {
  const { user, navigate, getAuthHeader, fetchAllData, fileInputRef, users, setUsers, userSearch, setUserSearch, filteredUsers, pendingFounders, founderSettings, setFounderSettings, founderSeatsRemaining, updateFounderSettings, toggleFounderEnrollment, toggleFoundingMember, suspendUser, archiveUser, permanentlyDeleteUser, changeUserTier, sales, pendingSales, pendingListingSales, handleVerifySale, handleVerifyListingSale, handleUnverifyListingSale, handleCancelListingSale, commissionDashboard, promoCodes, newPromo, setNewPromo, createPromoCode, deletePromoCode, promoToDelete, setPromoToDelete, potData, setPotData, updatePot, advertisers, pendingVideos, pendingLibraryVideos, handleBatchApproveVideos, handleVideoReview, handleLibraryVideoReview, moveDownlineData, setMoveDownlineData, featuredMembers, selectedUserToFeature, setSelectedUserToFeature, featuredText, setFeaturedText, handleAddFeaturedMember, handleRemoveFeaturedMember, tierFeatures, editingTier, setEditingTier, editFeatures, setEditFeatures, startEditingTier, saveTierFeatures, customTiers, showTierForm, setShowTierForm, newTier, setNewTier, tierFeaturesInput, setTierFeaturesInput, handleCreateTier, handleDeleteTier, pendingServices, flaggedServices, handleApproveService, handleFlagService, handleDeleteService, edirectoryStatus, syncingEdir, syncEdirectory, importedTransactions, importing, importResult, handleFileSelect, downloadSampleCSV } = props;

  return (
    <div className="space-y-4" data-testid="edirectory-tab">
      <h2 className="text-2xl font-playfair font-bold text-foreground mb-4 flex items-center gap-2">
                    <Globe className="w-6 h-6 text-green-500" /> Directory Integration
                  </h2>
            
                  {/* Status Card */}
                  <div className="wm-card p-6 rounded-lg border-2 border-green-500/30">
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="text-xl font-semibold text-foreground mb-2">Connection Status</h3>
                        <div className="flex items-center gap-4">
                          <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold ${
                            edirectoryStatus?.connected 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-red-500/20 text-red-400'
                          }`}>
                            {edirectoryStatus?.connected ? (
                              <><Check className="w-4 h-4" /> Connected</>
                            ) : (
                              <><X className="w-4 h-4" /> Not Connected</>
                            )}
                          </span>
                        </div>
                        {edirectoryStatus?.site_info?.name && (
                          <p className="text-muted-foreground mt-2">
                            Site: {edirectoryStatus.site_info.name}
                          </p>
                        )}
                      </div>
                      <Button
                        onClick={syncEdirectory}
                        disabled={syncingEdir}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid="sync-edirectory-btn"
                      >
                        <RefreshCw className={`w-4 h-4 mr-2 ${syncingEdir ? 'animate-spin' : ''}`} />
                        {syncingEdir ? 'Syncing...' : 'Sync Now'}
                      </Button>
                    </div>
                  </div>

                  {/* API Info */}
                  <div className="wm-card p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-foreground mb-4">API Configuration</h3>
                    <div className="grid md:grid-cols-2 gap-4 text-sm">
                      <div className="p-4 rounded bg-slate-800/50">
                        <p className="text-muted-foreground">Base URL</p>
                        <p className="text-foreground font-mono text-xs break-all">
                          {edirectoryStatus?.base_url || 'Not configured'}
                        </p>
                      </div>
                      <div className="p-4 rounded bg-slate-800/50">
                        <p className="text-muted-foreground">Directory Version</p>
                        <p className="text-foreground font-bold">
                          {edirectoryStatus?.edir_version || 'Unknown'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Sync Info */}
                  <div className="wm-card p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-foreground mb-4">Sync Information</h3>
                    <p className="text-muted-foreground text-sm">
                      The Directory sync will pull listing data and sales information from your Directory site.
                      This data is used to update the Millionaire Thermometer and Founder Leaderboard.
                    </p>
                  </div>

                  {/* CSV Import Section */}
                  <div className="wm-card p-6 rounded-lg border-2 border-blue-500/30">
                    <h3 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
                      <FileSpreadsheet className="w-5 h-5 text-blue-400" /> Import Transactions from CSV
                    </h3>
                    <p className="text-muted-foreground text-sm mb-4">
                      Export your transaction/invoice data from Directory and upload the CSV here.
                      The system will match sales reps by referral code — each member has a unique referral code.
                    </p>
              
                    <div className="flex flex-wrap gap-4 mb-6">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        accept=".csv"
                        className="hidden"
                        data-testid="csv-file-input"
                      />
                      <Button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={importing}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid="upload-csv-btn"
                      >
                        <Upload className={`w-4 h-4 mr-2 ${importing ? 'animate-pulse' : ''}`} />
                        {importing ? 'Importing...' : 'Upload CSV'}
                      </Button>
                      <Button
                        onClick={downloadSampleCSV}
                        variant="outline"
                        className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                        data-testid="download-sample-btn"
                      >
                        <Download className="w-4 h-4 mr-2" /> Download Sample CSV
                      </Button>
                    </div>

                    {/* CSV Format Info */}
                    <div className="p-4 rounded bg-slate-800/50 mb-4">
                      <p className="text-sm font-semibold text-foreground mb-2">Required CSV Columns:</p>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li><code className="text-blue-400">referral_code</code> - Member's unique referral code (primary match)</li>
                        <li><code className="text-blue-400">email</code> - Sales rep email (must match WeMasterminds member)</li>
                        <li><code className="text-blue-400">amount</code> - Transaction amount</li>
                        <li><code className="text-blue-400">paid</code> - Payment status (yes/no, true/false)</li>
                        <li><code className="text-blue-400">date</code> - Sale date (YYYY-MM-DD)</li>
                      </ul>
                    </div>

                    {/* Import Result */}
                    {importResult && (
                      <div className={`p-4 rounded border ${
                        importResult.transactions_created > 0 
                          ? 'bg-green-500/10 border-green-500/30' 
                          : 'bg-orange-100 border-orange-300'
                      }`}>
                        <h4 className="font-semibold text-foreground mb-2">Import Results</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-muted-foreground">Total Rows</p>
                            <p className="text-foreground font-bold">{importResult.total_rows}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Matched Members</p>
                            <p className="text-foreground font-bold">{importResult.matched_members}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Transactions Created</p>
                            <p className="text-green-400 font-bold">{importResult.transactions_created}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Unmatched</p>
                            <p className="text-orange-600 font-bold" data-testid="import-unmatched-count">
                              {(importResult.unmatched_emails?.length || 0) + (importResult.unmatched_codes?.length || 0)}
                            </p>
                          </div>
                        </div>
                        {importResult.unmatched_codes?.length > 0 && (
                          <div className="mt-4">
                            <p className="text-sm text-orange-600 flex items-center gap-2">
                              <AlertCircle className="w-4 h-4" /> Unmatched referral codes (not in WeMasterminds):
                            </p>
                            <div className="mt-2 max-h-32 overflow-y-auto">
                              {importResult.unmatched_codes.map((code, idx) => (
                                <span key={idx} className="inline-block text-xs bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded mr-2 mb-1">
                                  {code}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {importResult.mismatched_rows?.length > 0 && (
                          <div className="mt-4">
                            <p className="text-sm text-red-400 flex items-center gap-2">
                              <AlertCircle className="w-4 h-4" /> Skipped — referral code and email point to different members:
                            </p>
                            <div className="mt-2 max-h-32 overflow-y-auto space-y-1">
                              {importResult.mismatched_rows.map((msg, idx) => (
                                <p key={idx} className="text-xs bg-red-500/10 text-red-300 px-2 py-1 rounded">{msg}</p>
                              ))}
                            </div>
                          </div>
                        )}
                        {importResult.unmatched_emails?.length > 0 && (
                          <div className="mt-4">
                            <p className="text-sm text-orange-600 flex items-center gap-2">
                              <AlertCircle className="w-4 h-4" /> Unmatched emails (not in WeMasterminds):
                            </p>
                            <div className="mt-2 max-h-32 overflow-y-auto">
                              {importResult.unmatched_emails.map((email, idx) => (
                                <span key={idx} className="inline-block text-xs bg-yellow-500/20 text-yellow-400 px-2 py-1 rounded mr-2 mb-1">
                                  {email}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Recent Imported Transactions */}
                  {importedTransactions.length > 0 && (
                    <div className="wm-card p-6 rounded-lg">
                      <h3 className="text-lg font-semibold text-foreground mb-4">Recent Imported Transactions</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-border">
                              <th className="text-left py-2 text-muted-foreground">Email</th>
                              <th className="text-left py-2 text-muted-foreground">Referral Code</th>
                              <th className="text-left py-2 text-muted-foreground">Amount</th>
                              <th className="text-left py-2 text-muted-foreground">Profit</th>
                              <th className="text-left py-2 text-muted-foreground">Date</th>
                              <th className="text-left py-2 text-muted-foreground">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {importedTransactions.slice(0, 10).map((tx) => (
                              <tr key={tx.id} className="border-b border-border/50">
                                <td className="py-2 text-foreground">{tx.seller_email}</td>
                                <td className="py-2 text-blue-400 font-mono text-xs">{tx.seller_referral_code || '—'}</td>
                                <td className="py-2 text-primary">${tx.sale_amount?.toLocaleString()}</td>
                                <td className="py-2 text-green-400">${tx.profit_amount?.toLocaleString()}</td>
                                <td className="py-2 text-muted-foreground">{tx.original_date?.split('T')[0]}</td>
                                <td className="py-2">
                                  <span className={`px-2 py-0.5 rounded text-xs ${tx.is_paid ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                                    {tx.is_paid ? 'Paid' : 'Pending'}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
    </div>
  );
}

